// These widgets need to support autofocus, but it is used sparingly and actually enhances accessibility
/* eslint-disable jsx-a11y/no-autofocus */

import * as React from "react";
import {
  Checkbox,
  ChoiceGroup,
  getInputFocusStyle,
  getTheme,
  ICheckbox,
  IChoiceGroupOption,
  IStyleFunction,
  ITextFieldStyleProps,
  ITextFieldStyles,
  Label,
  Stack,
  TextField,
} from "@fluentui/react";
import { useId, useRefEffect } from "@fluentui/react-hooks";
import { WidgetProps } from "@rjsf/utils";
import { getLocalizedString } from "./LocalizedText";
import { setUserNameHint } from "../../actions/testInputActions";
import { useAppDispatch, useSelectStyles } from "../../hooks";

const theme = getTheme();

/**
 * Style function for when the text field is invalid
 *
 * @param props - the style props
 * @returns - the style
 */
export const textFieldInvalidStyle: IStyleFunction<ITextFieldStyleProps, ITextFieldStyles> = (props) => {
  const {
    disabled,
    focused,
    theme: { semanticColors, effects, palette },
    underlined,
  } = props;

  return {
    wrapper: [
      underlined && [
        {
          borderBottom: `1px solid ${semanticColors.errorText}`,
        },
        !disabled && {
          selectors: {
            ":hover": {
              borderBottomColor: semanticColors.errorText,
            },
          },
        },
        focused && [getInputFocusStyle(semanticColors.errorText, 0, "borderBottom")],
      ],
    ],
    fieldGroup: [
      focused && !underlined && getInputFocusStyle(semanticColors.errorText, effects.roundedCorner2),

      !underlined && {
        backgroundColor: semanticColors.inputBackground,
        borderColor: palette.red,
        selectors: {
          "&:hover": {
            borderColor: semanticColors.errorText,
          },
          ":before": {
            content: "",
          },
        },
      },
    ],
  };
};

/**
 * Style function for the normal style
 *
 * @param props - The style props
 * @returns - the style
 */
export const normalStyle: IStyleFunction<ITextFieldStyleProps, ITextFieldStyles> = (props) => {
  return {
    fieldGroup: [
      {
        backgroundColor: props.theme.semanticColors.inputBackground,
        ":before": {
          content: "",
        },
      },
    ],
  };
};

/**
 * The props for the base text widget
 */
interface IBaseTextWidgetProps extends WidgetProps {
  /**
   * The type of input element to render
   */
  type: "text" | "email" | "password";

  /**
   * Whether to disable the auto-trimming
   */
  disableTrim?: boolean;

  /**
   * The value
   */
  value: string | undefined;
}

/**
 * The base text widget
 *
 * @param props - the component props
 * @returns the markup
 */
export const BaseTextWidget: React.FC<IBaseTextWidgetProps> = (props) => {
  const {
    autofocus,
    disableTrim,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    options,
    options: { help },
    rawErrors,
    required,
    schema,
    type,
    value,
  } = props;

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    // If this field is marked as the username hint provider, publish it
    if (options.userNameHint && setUserNameHint && typeof value === "string") {
      dispatch(setUserNameHint(value));
    }
  }, [value]);

  const handleChange = (_ev: React.FormEvent<HTMLInputElement>, newValue: string | boolean | number | undefined) => {
    let valueToPublish: unknown;
    if (options.emptyValue !== undefined && newValue === "") {
      valueToPublish = options.emptyValue;
    } else if (typeof newValue === "string" && !disableTrim) {
      valueToPublish = newValue.trim();
    } else {
      valueToPublish = newValue;
    }

    onChange(valueToPublish);
  };

  const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    onBlur(id, ev.currentTarget.value);
  };

  const handleFocus = (ev: React.FocusEvent<HTMLInputElement>) => {
    onFocus(id, ev.currentTarget.value);
  };

  const descriptionId = schema.description ? id + "__description" : "";
  const helpId = help ? id + "__help" : "";
  const errorId = rawErrors ? id + "__error" : "";
  const ariaDescribedby = [errorId, descriptionId, helpId].join(" ");

  const styles = rawErrors ? textFieldInvalidStyle : normalStyle;

  return (
    <>
      {label && (
        <Label htmlFor={id}>
          {getLocalizedString(label)}
          {required && (
            <span className="required-field-indicator" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </Label>
      )}
      <TextField
        type={type}
        key={id}
        id={id}
        styles={styles}
        required={required}
        value={value}
        autoFocus={autofocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-describedby={ariaDescribedby}
        canRevealPassword={type === "password"}
        revealPasswordAriaLabel={ClientStrings.RevealPassword}
      />
    </>
  );
};

/**
 * The text widget
 *
 * @param props - the component properties
 * @returns the component
 */
export const TextWidget: React.FC<WidgetProps> = (props) => {
  return <BaseTextWidget type="text" {...props} />;
};

/**
 * The email widget
 *
 * @param props - the component properties
 * @returns the component
 */
export const EmailWidget: React.FC<WidgetProps> = (props) => {
  return <BaseTextWidget type="email" {...props} />;
};

/**
 * The password widget
 *
 * @param props - the component props
 * @returns the markup
 */
export const PasswordWidget: React.FC<WidgetProps> = (props) => {
  return <BaseTextWidget type="password" {...props} disableTrim={true} />;
};

/**
 * The checkbox widget props contract
 */
interface ICheckboxWidgetProps extends WidgetProps {
  /**
   * The checkbox value
   */
  value: boolean | undefined;
}

/**
 * The checkbox widget
 *
 * @param props - the component props
 * @returns the markup
 */
export const CheckboxWidget: React.FC<ICheckboxWidgetProps> = (props) => {
  const {
    autofocus,
    className,
    id,
    label,
    onChange,
    options: { help },
    rawErrors,
    required,
    schema,
    value,
  } = props;

  const descriptionId = schema.description ? id + "__description" : "";
  const helpId = help ? id + "__help" : "";
  const errorId = rawErrors ? id + "__error" : "";
  const ariaDescribedby = [errorId, descriptionId, helpId].join(" ");
  const isRequiredConsentCheckbox = required && schema.title === "TestAccountConsent";

  const descriptionComponent = (
    <span aria-hidden="true" className="ms-Checkbox-text" style={{ paddingInlineStart: 4 }}>
      {getLocalizedString(label)}
      {required && <span className="required-field-indicator"> * </span>}
    </span>
  );

  const checkboxRef = useRefEffect<ICheckbox>((checkbox) => {
    if (autofocus) {
      checkbox.focus();
    }
  });

  return (
    <Stack className={className} styles={{ root: { paddingTop: 5, paddingBottom: 5 } }}>
      <Checkbox
        ariaLabel={getLocalizedString(label)}
        ariaDescribedBy={ariaDescribedby}
        onRenderLabel={() => descriptionComponent}
        id={id}
        componentRef={checkboxRef}
        required={isRequiredConsentCheckbox}
        checked={value}
        onChange={(_event, newValue): void => onChange(newValue)}
        styles={{
          checkbox: {
            alignSelf: "center",
            borderColor: rawErrors?.length ? `${theme.semanticColors.errorText} !important` : "",
          },
        }}
      />
    </Stack>
  );
};

/**
 * An option for a drop-down enumeration
 */
interface IEnumOption {
  /**
   * The label for the dropdown option
   */
  label: string;

  /**
   * The value for the dropdown option
   */
  value: string;
}

/**
 * The SelectWidget props contract
 */
interface ISelectWidgetProps extends WidgetProps {
  /**
   * The select value
   */
  value: string | undefined;
}

/**
 * The select widget
 *
 * @param props - The component props
 * @returns the markup
 */
export const SelectWidget: React.FC<ISelectWidgetProps> = (props) => {
  const {
    autofocus,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    options: { help },
    rawErrors,
    required,
    schema,
    value,
  } = props;
  const enumOptions = props.options.enumOptions as IEnumOption[];
  const dropdownRef = useRefEffect<HTMLSelectElement>((select) => {
    if (autofocus) {
      select.focus();
    }
  });

  const descriptionId = schema.description ? id + "__description" : "";
  const helpId = help ? id + "__help" : "";
  const errorId = rawErrors ? id + "__error" : "";
  const ariaDescribedby = [errorId, descriptionId, helpId].join(" ");

  const handleChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(ev.target.value);
  };

  const handleBlur = (ev: React.FocusEvent<HTMLSelectElement>) => {
    onBlur(id, ev.currentTarget.value);
  };

  const handleFocus = (ev: React.FocusEvent<HTMLSelectElement>) => {
    onFocus(id, ev.currentTarget.value);
  };

  const options = enumOptions.map((o) => ({
    key: o.value,
    text: o.label ? getLocalizedString(o.label) : o.value,
  }));

  const selectStyles = useSelectStyles();

  return (
    <>
      {label && (
        <Label htmlFor={id}>
          {getLocalizedString(label)}
          {required && (
            <span className="required-field-indicator" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </Label>
      )}
      <select
        className={selectStyles}
        ref={dropdownRef}
        id={id}
        value={value}
        required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-describedby={ariaDescribedby}
      >
        {options.map((o) => {
          const isSelected = o.key === value;
          return (
            <option aria-selected={isSelected} key={o.key} value={o.key}>
              {o.text}
            </option>
          );
        })}
      </select>
    </>
  );
};

/**
 * The RadioWidget props contract
 */
interface IRadioWidgetProps extends WidgetProps {
  /**
   * The value of the radio widget
   */
  value: string | undefined;
}

/**
 * The radio widget
 *
 * @param props - the component props
 * @returns the markup
 */
export const RadioWidget: React.FC<IRadioWidgetProps> = (props) => {
  const {
    autofocus,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    options: { help },
    rawErrors,
    required,
    schema,
    value,
  } = props;

  const enumOptions = props.options.enumOptions as IEnumOption[];
  const currentTheme = getTheme();
  const resolvedThemeName = typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") : null;
  const isClassicTheme = resolvedThemeName === "classic";
  const { enumHelpers = [] } = schema as {
    /** Helper info for enum values */
    enumHelpers: string[];
  };
  const descriptionId = schema.description ? id + "__description" : "";
  const helpId = help ? id + "__help" : "";
  const errorId = rawErrors ? id + "__error" : "";
  const ariaDescribedby = [errorId, descriptionId, helpId].join(" ");

  const handleChange = (_ev: React.FormEvent<HTMLInputElement>, selectedOption: IChoiceGroupOption) => {
    onChange(selectedOption.key);
  };

  const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    onBlur(id, ev.currentTarget.value);
  };

  const handleFocus = (ev: React.FocusEvent<HTMLInputElement>) => {
    onFocus(id, ev.currentTarget.value);
  };

  const radioOptionStyles = {
    choiceFieldWrapper: {
      backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : undefined,
      borderRadius: 2,
    },
    field: {
      backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : undefined,
      borderRadius: 2,
      selectors: {
        ":before": {
          backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : currentTheme.semanticColors.bodyBackground,
        },
        ":hover:before": {
          backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : currentTheme.semanticColors.bodyBackground,
        },
        ":focus:before": {
          backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : currentTheme.semanticColors.bodyBackground,
        },
      },
    },
    innerField: {
      backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : undefined,
      borderRadius: 2,
    },
    labelWrapper: {
      backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : undefined,
      borderRadius: 2,
    },
  };

  const options: IChoiceGroupOption[] = enumOptions.map((o, i) => ({
    key: o.value,
    text: o.label ? getLocalizedString(o.label) : o.value,
    styles: radioOptionStyles,
    onRenderField: (props, render) => {
      return (
        <div
          style={{
            backgroundColor: isClassicTheme ? currentTheme.semanticColors.inputBackground : undefined,
            borderRadius: 2,
            display: "block",
            paddingTop: 2,
            paddingRight: 4,
            paddingBottom: 2,
          }}
        >
          {render?.(props)}
          {props?.checked && enumHelpers[i] ? (
            <div style={{ paddingTop: 8, paddingBottom: 6, paddingLeft: 26, fontStyle: "italic" }}>
              {getLocalizedString(enumHelpers[i])}
            </div>
          ) : null}
        </div>
      );
    },
  }));

  if (autofocus && options.length > 0) {
    options[0].autoFocus = true;
  }

  const groupLabelId = useId("groupLabel");

  return (
    <>
      {label && (
        <Label id={groupLabelId} htmlFor={id}>
          {getLocalizedString(label)}
          {required && (
            <span className="required-field-indicator" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </Label>
      )}
      <ChoiceGroup
        id={id}
        aria-labelledby={groupLabelId}
        selectedKey={value}
        required={required}
        options={options}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-describedby={ariaDescribedby}
      />
    </>
  );
};
